﻿import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { LoginStateService } from './login-state.service';

export interface CompoundListSummary {
  id: number;
  name: string;
  item_count: number;
  created_on: string;
}

export interface CompoundListItem {
  compound_id: string;
  label: string;
  added_on: string;
}

@Injectable()
export class CompoundListsService {
  private email: string = null;

  lists$: BehaviorSubject<CompoundListSummary[]> = new BehaviorSubject<CompoundListSummary[]>([]);

  constructor(private http: HttpClient, private loginState: LoginStateService) {
    loginState.isUserLoggedIn.subscribe(state => {
      if (state.loggedIn && state.email) {
        this.email = state.email;
        this.load();
      } else {
        this.email = null;
        this.lists$.next([]);
      }
    });
  }

  private get authHeaders(): HttpHeaders {
    return new HttpHeaders().set('Authorization', localStorage.getItem('auth_token') || '');
  }

  createList(name: string): Observable<CompoundListSummary | null> {
    if (!this.email) { return of(null); }
    return this.http.post<{ success: boolean; data: CompoundListSummary }>(
      'https://crestdb.org/api/compound_lists', { email: this.email, name: name }, { headers: this.authHeaders }
    ).pipe(
      map(res => {
        if (res.success) {
          this.lists$.next([res.data, ...this.lists$.value]);
          return res.data;
        }
        return null;
      }),
      catchError(() => of(null))
    );
  }

  deleteList(listId: number): void {
    if (!this.email) { return; }
    const prev = this.lists$.value;
    this.lists$.next(prev.filter(l => l.id !== listId));
    this.http.delete(`https://crestdb.org/api/compound_lists/${listId}?email=${encodeURIComponent(this.email)}`, { headers: this.authHeaders })
      .subscribe({ error: () => { this.lists$.next(prev); } });
  }

  addToList(listId: number, compoundId: string, label: string): void {
    if (!this.email) { return; }
    this.http.post(
      `https://crestdb.org/api/compound_lists/${listId}/items`,
      { compound_id: compoundId, label: label },
      { headers: this.authHeaders }
    ).subscribe({
      next: () => {
        this.lists$.next(this.lists$.value.map(l =>
          l.id === listId ? Object.assign({}, l, { item_count: l.item_count + 1 }) : l
        ));
      },
      error: () => {}
    });
  }

  removeFromList(listId: number, compoundId: string): void {
    if (!this.email) { return; }
    this.http.delete(
      `https://crestdb.org/api/compound_lists/${listId}/items/${encodeURIComponent(compoundId)}`,
      { headers: this.authHeaders }
    ).subscribe({
      next: () => {
        this.lists$.next(this.lists$.value.map(l =>
          l.id === listId ? Object.assign({}, l, { item_count: Math.max(0, l.item_count - 1) }) : l
        ));
      },
      error: () => {}
    });
  }

  getItems(listId: number): BehaviorSubject<CompoundListItem[]> {
    const subject = new BehaviorSubject<CompoundListItem[]>([]);
    this.http.get<{ success: boolean; data: CompoundListItem[] }>(
      `https://crestdb.org/api/compound_lists/${listId}/items`, { headers: this.authHeaders }
    ).subscribe({
      next: (res) => { if (res.success) { subject.next(res.data); } },
      error: () => {}
    });
    return subject;
  }

  private load(): void {
    this.http.get<{ success: boolean; data: CompoundListSummary[] }>(
      `https://crestdb.org/api/compound_lists?email=${encodeURIComponent(this.email)}`, { headers: this.authHeaders }
    ).subscribe({
      next: (res) => { if (res.success) { this.lists$.next(res.data); } },
      error: () => {}
    });
  }
}
